import {
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	ResponsiveContainer,
	Tooltip,
	Legend,
} from 'recharts'

// Color palette for charts
const COLORS = [
	'#8b5cf6', // violet
	'#22c55e', // green
	'#f59e0b', // amber
	'#ef4444', // red
	'#3b82f6', // blue
	'#ec4899', // pink
	'#14b8a6', // teal
	'#f97316', // orange
]

// Breakdown item structure from API
interface BreakdownItem {
	openers: number
	players: number
	gameName?: string
}

interface FlexBreakdownChartsProps {
	walletTypeBreakdown: Record<string, BreakdownItem> | Record<string, never>
	balanceRangeBreakdown: Record<string, BreakdownItem> | Record<string, never>
	gameBreakdown: Record<string, BreakdownItem> | Record<string, never>
}

function transformBreakdownData(
	breakdown: Record<string, BreakdownItem> | Record<string, never>,
	useGameName = false,
): { name: string; value: number; displayName?: string }[] {
	if (!breakdown || typeof breakdown !== 'object') return []

	return Object.entries(breakdown)
		.filter(([, item]) => {
			if (typeof item !== 'object' || item === null) return false
			const typedItem = item as BreakdownItem
			return typedItem.openers > 0 || typedItem.players > 0
		})
		.map(([name, item]) => {
			const typedItem = item as BreakdownItem
			return {
				name: useGameName && typedItem.gameName ? typedItem.gameName : name,
				value: typedItem.openers, // Use openers count as the main value
				displayName:
					useGameName && typedItem.gameName ? typedItem.gameName : undefined,
			}
		})
		.sort((a, b) => b.value - a.value)
}

// Wallet type label mapping
const WALLET_TYPE_LABELS: Record<string, string> = {
	MAIN: 'กระเป๋า',
	BONUS: 'โบนัส',
	PROMO: 'โปรโมชัน',
	FREE_CREDIT: 'เครดิตฟรี',
}

// Balance range label mapping
const BALANCE_RANGE_LABELS: Record<string, string> = {
	'0': '฿0',
	'0-100': '฿0-100',
	'100-500': '฿100-500',
	'500-1000': '฿500-1,000',
	'1000-5000': '฿1,000-5,000',
	'5000-10000': '฿5,000-10,000',
	'10000+': '฿10,000+',
}

function getLabel(key: string, type: 'wallet' | 'balance' | 'game'): string {
	if (type === 'wallet') {
		return WALLET_TYPE_LABELS[key] || key
	}
	if (type === 'balance') {
		return BALANCE_RANGE_LABELS[key] || key
	}
	return key
}

interface MiniPieChartProps {
	data: { name: string; value: number }[]
	title: string
	labelType: 'wallet' | 'balance' | 'game'
}

function MiniPieChart({ data, title, labelType }: MiniPieChartProps) {
	if (data.length === 0) {
		return (
			<div className="flex flex-col items-center">
				<h4 className="mb-2 text-sm font-medium text-muted-foreground">
					{title}
				</h4>
				<div className="flex h-[180px] w-full items-center justify-center">
					<p className="text-sm text-muted-foreground">ไม่มีข้อมูล</p>
				</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col items-center">
			<h4 className="mb-2 text-sm font-medium text-muted-foreground">
				{title}
			</h4>
			<ResponsiveContainer width="100%" height={180}>
				<PieChart>
					<Pie
						data={data}
						cx="50%"
						cy="50%"
						innerRadius={35}
						outerRadius={60}
						paddingAngle={2}
						dataKey="value"
						nameKey="name"
					>
						{data.map((_, index) => (
							<Cell
								key={`cell-${index}`}
								fill={COLORS[index % COLORS.length]}
							/>
						))}
					</Pie>
					<Tooltip
						contentStyle={{
							backgroundColor: 'hsl(var(--card))',
							border: '1px solid hsl(var(--border))',
							borderRadius: '8px',
							fontSize: '12px',
						}}
						formatter={(value: number, name: string) => [
							value.toLocaleString('th-TH'),
							getLabel(name, labelType),
						]}
					/>
					<Legend
						layout="vertical"
						align="right"
						verticalAlign="middle"
						wrapperStyle={{ fontSize: '11px' }}
						formatter={(value: string) => getLabel(value, labelType)}
					/>
				</PieChart>
			</ResponsiveContainer>
		</div>
	)
}

interface MiniBarChartProps {
	data: { name: string; value: number }[]
	title: string
	labelType: 'wallet' | 'balance' | 'game'
}

function MiniBarChart({ data, title, labelType }: MiniBarChartProps) {
	if (data.length === 0) {
		return (
			<div className="flex flex-col items-center">
				<h4 className="mb-2 text-sm font-medium text-muted-foreground">
					{title}
				</h4>
				<div className="flex h-[180px] w-full items-center justify-center">
					<p className="text-sm text-muted-foreground">ไม่มีข้อมูล</p>
				</div>
			</div>
		)
	}

	const chartData = data.map((item) => ({
		...item,
		displayName: getLabel(item.name, labelType),
	}))

	return (
		<div className="flex flex-col items-center">
			<h4 className="mb-2 text-sm font-medium text-muted-foreground">
				{title}
			</h4>
			<ResponsiveContainer width="100%" height={180}>
				<BarChart
					data={chartData}
					layout="vertical"
					margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
				>
					<XAxis
						type="number"
						tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
						tickFormatter={(value) => value.toLocaleString('th-TH')}
					/>
					<YAxis
						type="category"
						dataKey="displayName"
						tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
						width={80}
					/>
					<Tooltip
						contentStyle={{
							backgroundColor: 'hsl(var(--card))',
							border: '1px solid hsl(var(--border))',
							borderRadius: '8px',
							fontSize: '12px',
						}}
						formatter={(value: number) => [
							value.toLocaleString('th-TH'),
							'จำนวน',
						]}
					/>
					<Bar dataKey="value" radius={[0, 4, 4, 0]}>
						{chartData.map((_, index) => (
							<Cell
								key={`cell-${index}`}
								fill={COLORS[index % COLORS.length]}
							/>
						))}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	)
}

export default function FlexBreakdownCharts({
	walletTypeBreakdown,
	balanceRangeBreakdown,
	gameBreakdown,
}: FlexBreakdownChartsProps) {
	const walletData = transformBreakdownData(walletTypeBreakdown)
	const balanceData = transformBreakdownData(balanceRangeBreakdown)
	const gameData = transformBreakdownData(gameBreakdown, true) // Use gameName for display

	const hasAnyData =
		walletData.length > 0 || balanceData.length > 0 || gameData.length > 0

	if (!hasAnyData) {
		return (
			<div className="flex items-center justify-center py-8">
				<p className="text-sm text-muted-foreground">ไม่มีข้อมูล Breakdown</p>
			</div>
		)
	}

	return (
		<div className="grid gap-6 md:grid-cols-3">
			<MiniPieChart
				data={walletData}
				title="ประเภทกระเป๋า"
				labelType="wallet"
			/>
			<MiniBarChart
				data={balanceData}
				title="ช่วงยอดเงิน"
				labelType="balance"
			/>
			<MiniPieChart data={gameData} title="เกมที่เล่น" labelType="game" />
		</div>
	)
}
